// ============================================================
// 상세 페이지 chrome 저작 — 속성 편집 패널(선택 버튼 + 숨김 목록) (F8 팝아웃)
// ============================================================
//
// F8 미리보기 팝아웃 창의 chrome 편집 모드에서, 팝아웃 바(헤더/컨트롤 바)의 버튼을 클릭하면 그 버튼이
// 선택되고 이 패널이 그 버튼 1개의 상태별 속성(노출/슬롯/순서/라벨/오버플로우)을 편집한다. 상태/선택은
// 팝아웃이 소유하고, onChange는 채널로 부모(EditorPage)에 전달돼 template.detailChrome를 갱신한다.
//
// - template.detailChrome(DetailChromeConfig) 미지정이면 상세 페이지가 코드 레지스트리
//   (DETAIL_CHROME_ACTIONS) 기본 배치로 렌더 — 회귀 안전. 편집 시작 시 materialize(기본 clone).
// - **동작(behavior)은 코드 소유** — 여기선 표시만. mandatory 액션은 숨길 수 없다(노출 토글 비활성).
// - 기록 구조(statusOverrides[status] = {hidden?,slot?,order?,label?})는 web 해소
//   (apps/web/.../resolveDetailChromeActions.ts) 및 rc resolveDetailChromePlacement와 정확히 일치.
// 결정: docs/decisions/2026-07-21-compose-chrome-per-state-detail

import { useCallback, useMemo } from 'react';
import type {
  DetailChromeActionId,
  DetailChromeConfig,
  DetailChromeItem,
  DetailChromeItemStatusOverride,
  DetailChromeOverflow,
  DetailChromeSlot,
  DetailChromeStatus,
} from '@safetyhub/shared';
import {
  DETAIL_CHROME_ACTION_LABELS,
  DETAIL_CHROME_ACTIONS,
  DETAIL_CHROME_CONDITION_VARS,
  DETAIL_CHROME_STATUS_LABELS,
} from '@safetyhub/shared';
import {
  resolveDetailChromeLayout,
  resolveDetailChromeUnsupportedReason,
} from '@safetyhub/report-composer';
import type { DetailChromeTemplateCapabilities } from '@safetyhub/report-composer';
import { Card } from '@safetyhub/ui';

/** 조건 변수 어휘 → 단일 절 조건식 옵션(select). boolean=truthy(key), enum=key === 'value'. */
const CONDITION_OPTIONS: { label: string; value: string }[] = DETAIL_CHROME_CONDITION_VARS.flatMap(
  (v) =>
    v.type === 'boolean'
      ? [{ label: v.label, value: v.key }]
      : (v.enumValues ?? []).map((ev) => ({
          label: `${v.label} = ${ev}`,
          value: `${v.key} === '${ev}'`,
        })),
);

/** 게이팅 조건식 편집 필드 — 어휘 select(공통) + 직접 입력(고급, &&/|| 복합식). */
function ConditionField({
  label,
  value,
  onChange,
  hint,
}: {
  label: string;
  value?: string;
  onChange: (next: string | undefined) => void;
  hint?: string;
}) {
  const matched = CONDITION_OPTIONS.find((o) => o.value === value);
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1.5 text-xs text-gray-600">
        <span className="w-16 shrink-0 font-medium">{label}</span>
        <select
          value={matched ? value : value ? '__custom__' : ''}
          onChange={(e) => {
            const v = e.target.value;
            if (v === '') onChange(undefined);
            else if (v !== '__custom__') onChange(v);
            // __custom__ 선택은 아래 텍스트 입력 유지(no-op).
          }}
          className="flex-1 rounded border border-gray-300 px-1.5 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-brand-400"
        >
          <option value="">없음(항상)</option>
          {CONDITION_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
          {value && !matched && <option value="__custom__">직접 입력</option>}
        </select>
      </div>
      <input
        type="text"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || undefined)}
        placeholder="조건식 직접 입력 (예: contactSignatureComplete && authorSigned)"
        className="rounded border border-gray-200 px-1.5 py-0.5 font-mono text-[11px] text-gray-600 focus:outline-none focus:ring-1 focus:ring-brand-400"
      />
      {hint && <p className="text-[10px] text-gray-400">{hint}</p>}
    </div>
  );
}

/**
 * 코드 소유(편집 불가) 기본 활성 조건 — 라이프사이클 무결성 규칙이라 저작 대상 아님. 저작 '활성 조건'
 * (enableWhen)은 이 위에 AND로 추가된다. 미리보기의 built-in enable(재제출=editedAfterRejection 등)과 대응.
 */
const CODE_ENABLE_CONDITIONS: Partial<Record<DetailChromeActionId, string>> = {
  resubmit: '반려 후 본문 수정됨(editedAfterRejection)',
};

/**
 * 이 액션이 **양식 능력** 때문에 도달 불가인가(위임 미지원 양식의 위임 동사, 제출 후 흐름
 * 미선언 양식의 재진입).
 *
 * 판정은 `resolveDetailChromeLayout`이 쓰는 **바로 그 함수**를 부른다 — 종전엔 여기 같은 판정을
 * 손으로 복제하고 있었고, 능력 축이 둘이 된 지금은 그 복제가 곧 드리프트다(미리보기 바에서는
 * 사라진 버튼의 속성 패널이 여기서는 열리는 식으로 갈린다).
 */
function isUnsupportedByTemplate(
  actionId: DetailChromeActionId,
  capabilities: DetailChromeTemplateCapabilities | undefined,
): boolean {
  return resolveDetailChromeUnsupportedReason(actionId, capabilities) !== null;
}

/** 슬롯 select 옵션 — headerLeft/headerRight/actionBar. */
const SLOT_OPTIONS: { value: DetailChromeSlot; label: string }[] = [
  { value: 'headerLeft', label: '헤더 좌측' },
  { value: 'headerRight', label: '헤더 우측' },
  { value: 'actionBar', label: '하단 액션바' },
];

/**
 * config 미구성(value undefined) 시 편집 진입 시점에 레지스트리 기본으로 materialize한다.
 * actionId당 1개 아이템: base slot/order = 레지스트리 기본, statusOverrides는 빈 델타.
 */
function materializeConfig(): DetailChromeConfig {
  const items: DetailChromeItem[] = (Object.keys(DETAIL_CHROME_ACTIONS) as DetailChromeActionId[])
    // opt-in(defaultHidden) 액션은 기본 materialize에서 제외 — 저작자가 명시 추가할 때만 포함.
    .filter((actionId) => !DETAIL_CHROME_ACTIONS[actionId].defaultHidden)
    .map((actionId) => {
      const meta = DETAIL_CHROME_ACTIONS[actionId];
      return {
        id: actionId,
        actionId,
        slot: meta.defaultSlot,
        order: meta.defaultOrder,
        statusOverrides: {},
      };
    });
  return { items };
}

/** 이 상태에서 actionId의 유효 라벨(override → 기본). */
function effectiveLabelOf(
  config: DetailChromeConfig | undefined,
  status: DetailChromeStatus,
  actionId: DetailChromeActionId,
): string {
  const item = config?.items.find((i) => i.actionId === actionId);
  const ov = item?.statusOverrides?.[status];
  return ov?.label ?? item?.label ?? DETAIL_CHROME_ACTION_LABELS[actionId];
}

/** 오버라이드된 속성 배지(ComposeChromeEditor와 동형). */
function OverrideBadge() {
  return (
    <span className="rounded bg-amber-100 px-1 text-[10px] font-medium text-amber-700">
      재정의
    </span>
  );
}

/** "기본으로" 리셋 버튼(해당 상태 오버라이드 키 삭제). */
function ResetLink({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-[10px] text-brand-600 hover:underline"
    >
      기본으로
    </button>
  );
}

/**
 * detailChrome 속성 편집 패널 — 선택 버튼 속성 + 숨김 목록. 상태/선택은 팝아웃 바가 소유(상위).
 *
 * @param value    현재 template.detailChrome(미지정이면 코드 기본 배치 사용 중)
 * @param onChange 갱신된 DetailChromeConfig 또는 undefined(구성 제거=코드 기본)
 * @param status   팝아웃 바에서 선택된 상태
 * @param selected 팝아웃 바에서 선택된 버튼(없으면 null)
 * @param onSelect 버튼 선택 변경(숨김 목록 클릭 → 선택)
 * @param capabilities 양식 능력(위임 지원 여부) — 도달 불가 액션을 최대 집합에서 제외하고 사유를 안내
 */
export function DetailChromePropertyEditor({
  value,
  onChange,
  status,
  selected,
  onSelect,
  capabilities,
}: {
  value?: DetailChromeConfig;
  onChange: (next: DetailChromeConfig | undefined) => void;
  status: DetailChromeStatus;
  selected: DetailChromeActionId | null;
  onSelect: (actionId: DetailChromeActionId | null) => void;
  capabilities?: DetailChromeTemplateCapabilities;
}) {
  const reset = useCallback(() => {
    onChange(undefined);
    onSelect(null);
  }, [onChange, onSelect]);

  // 해당 actionId 아이템을 갱신(없으면 레지스트리 기본으로 materialize 후 갱신). 항상 불변.
  const updateItem = useCallback(
    (
      base: DetailChromeConfig,
      actionId: DetailChromeActionId,
      updater: (item: DetailChromeItem) => DetailChromeItem,
    ) => {
      const idx = base.items.findIndex((it) => it.actionId === actionId);
      const meta = DETAIL_CHROME_ACTIONS[actionId];
      const item: DetailChromeItem =
        idx >= 0
          ? base.items[idx]
          : { id: actionId, actionId, slot: meta.defaultSlot, order: meta.defaultOrder };
      const nextItem = updater(item);
      const nextItems =
        idx >= 0
          ? base.items.map((it, i) => (i === idx ? nextItem : it))
          : [...base.items, nextItem];
      onChange({ ...base, items: nextItems });
    },
    [onChange],
  );

  // 선택 상태의 statusOverrides[status] 델타 갱신. patch 값이 undefined면 그 키 삭제(기본으로).
  // 상태 오버라이드가 비면 그 상태 키 제거. value 미지정이면 먼저 materialize.
  const patchOverride = useCallback(
    (actionId: DetailChromeActionId, patch: Partial<DetailChromeItemStatusOverride>) => {
      const base = value ?? materializeConfig();
      updateItem(base, actionId, (item) => {
        const overrides: Partial<Record<DetailChromeStatus, DetailChromeItemStatusOverride>> = {
          ...(item.statusOverrides ?? {}),
        };
        const cur: DetailChromeItemStatusOverride = { ...(overrides[status] ?? {}) };
        (Object.keys(patch) as (keyof DetailChromeItemStatusOverride)[]).forEach((k) => {
          if (patch[k] === undefined) delete cur[k];
          else (cur[k] as DetailChromeItemStatusOverride[typeof k]) = patch[k];
        });
        if (Object.keys(cur).length === 0) delete overrides[status];
        else overrides[status] = cur;
        return { ...item, statusOverrides: overrides };
      });
    },
    [value, updateItem, status],
  );

  // 버튼별 오버플로우 정책(base 필드, 상태 무관). auto는 undefined로 저장(기본 최소화). value 미지정이면 materialize.
  const setOverflow = useCallback(
    (actionId: DetailChromeActionId, next: DetailChromeOverflow) => {
      const base = value ?? materializeConfig();
      // 액션 기본값(defaultOverflow ?? auto)과 같으면 undefined로 저장(기본 최소화).
      const def = DETAIL_CHROME_ACTIONS[actionId].defaultOverflow ?? 'auto';
      updateItem(base, actionId, (item) => ({
        ...item,
        overflow: next === def ? undefined : next,
      }));
    },
    [value, updateItem],
  );

  // 범용 게이팅 조건(base 필드, 상태 무관) — enableWhen/visibleWhen/disabledReason. 빈 값=키 삭제(기본 최소화).
  const setCondition = useCallback(
    (
      actionId: DetailChromeActionId,
      field: 'enableWhen' | 'visibleWhen' | 'disabledReason' | 'primaryWhen',
      val: string | undefined,
    ) => {
      const base = value ?? materializeConfig();
      updateItem(base, actionId, (item) => {
        const next: DetailChromeItem = { ...item };
        if (val) next[field] = val;
        else delete next[field];
        return next;
      });
    },
    [value, updateItem],
  );

  // 같은 슬롯 내 순서 이동(↑↓) — 인접 형제와 base order 교환. 미존재 항목은 레지스트리 기본으로 생성.
  const slotOf = useCallback(
    (base: DetailChromeConfig, actionId: DetailChromeActionId): DetailChromeSlot => {
      const it = base.items.find((i) => i.actionId === actionId);
      return it?.statusOverrides?.[status]?.slot ?? it?.slot ?? DETAIL_CHROME_ACTIONS[actionId].defaultSlot;
    },
    [status],
  );
  const moveItem = useCallback(
    (actionId: DetailChromeActionId, dir: -1 | 1) => {
      const base = value ?? materializeConfig();
      const layout = resolveDetailChromeLayout(base, status, { capabilities });
      const slotItems = layout[slotOf(base, actionId)];
      const idx = slotItems.findIndex((i) => i.actionId === actionId);
      const swapIdx = idx + dir;
      if (idx < 0 || swapIdx < 0 || swapIdx >= slotItems.length) return; // 경계
      const neighborId = slotItems[swapIdx].actionId;
      const orderOf = (id: DetailChromeActionId) =>
        base.items.find((i) => i.actionId === id)?.order ?? DETAIL_CHROME_ACTIONS[id].defaultOrder;
      const aOrder = orderOf(actionId);
      const bOrder = orderOf(neighborId);
      const ensure = (items: DetailChromeItem[], id: DetailChromeActionId) =>
        items.some((i) => i.actionId === id)
          ? items
          : [
              ...items,
              {
                id,
                actionId: id,
                slot: DETAIL_CHROME_ACTIONS[id].defaultSlot,
                order: DETAIL_CHROME_ACTIONS[id].defaultOrder,
              },
            ];
      let items = ensure(ensure(base.items, actionId), neighborId);
      items = items.map((i) =>
        i.actionId === actionId
          ? { ...i, order: bOrder }
          : i.actionId === neighborId
            ? { ...i, order: aOrder }
            : i,
      );
      onChange({ ...base, items });
    },
    [value, status, slotOf, onChange, capabilities],
  );
  // 선택 버튼의 이동 가능 여부(슬롯 경계).
  const moveInfo = useMemo(() => {
    if (!selected) return { canUp: false, canDown: false };
    const base = value ?? materializeConfig();
    const slotItems = resolveDetailChromeLayout(base, status, { capabilities })[
      slotOf(base, selected)
    ];
    const idx = slotItems.findIndex((i) => i.actionId === selected);
    return { canUp: idx > 0, canDown: idx >= 0 && idx < slotItems.length - 1 };
  }, [selected, value, status, slotOf, capabilities]);

  // opt-in 액션 추가 — config에 바 항목 생성(updateItem이 미존재 시 레지스트리 기본으로 생성) + 선택.
  const addItem = useCallback(
    (actionId: DetailChromeActionId) => {
      const base = value ?? materializeConfig();
      updateItem(base, actionId, (item) => item); // identity updater → 미존재면 생성.
      onSelect(actionId);
    },
    [value, updateItem, onSelect],
  );

  // opt-in 액션 제거 — config 항목 삭제(defaultHidden이라 삭제 시 '추가 가능한 버튼'으로 복귀).
  const removeItem = useCallback(
    (actionId: DetailChromeActionId) => {
      if (!value) return; // config 없으면 항목도 없음(no-op).
      onChange({ ...value, items: value.items.filter((i) => i.actionId !== actionId) });
      onSelect(null);
    },
    [value, onChange, onSelect],
  );

  // 이 상태 eligible 중 배치 안 된 것 → opt-in(defaultHidden·config 미포함)과 숨김(그 외)으로 분리.
  const { optInIds, hiddenIds } = useMemo(() => {
    const layout = resolveDetailChromeLayout(value, status, { capabilities });
    const placed = new Set<DetailChromeActionId>();
    (Object.keys(layout) as DetailChromeSlot[]).forEach((slot) =>
      layout[slot].forEach((it) => placed.add(it.actionId)),
    );
    const inConfig = (id: DetailChromeActionId) => !!value?.items.some((i) => i.actionId === id);
    const eligibleUnplaced = (Object.keys(DETAIL_CHROME_ACTIONS) as DetailChromeActionId[]).filter(
      (id) =>
        DETAIL_CHROME_ACTIONS[id].statuses.includes(status) &&
        !placed.has(id) &&
        // 양식 능력 미충족(위임 미지원)은 "숨긴 버튼"이 아니다 — 저작자가 되살릴 수 있는 축이
        // 아니라 아예 존재할 수 없는 버튼이라, 아래 전용 안내 블록이 사유와 출구를 대신 맡는다.
        !isUnsupportedByTemplate(id, capabilities),
    );
    return {
      optInIds: eligibleUnplaced.filter(
        (id) => DETAIL_CHROME_ACTIONS[id].defaultHidden && !inConfig(id),
      ),
      hiddenIds: eligibleUnplaced.filter(
        (id) => !(DETAIL_CHROME_ACTIONS[id].defaultHidden && !inConfig(id)),
      ),
    };
  }, [value, status, capabilities]);

  // 이 상태에서 "양식이 지원하지 않아" 그려지지 않는 버튼들 — 사유·출구 안내용(비대화형).
  // 능력 축이 둘이 됐으므로(위임 지원 / 제출 후 흐름 선언) **사유별로 묶어** 보여 준다 —
  // 한 문장만 적으면 다른 축 때문에 빠진 버튼에 엉뚱한 출구를 안내하게 된다.
  const unsupportedGroups = useMemo(() => {
    const byReason = new Map<string, DetailChromeActionId[]>();
    (Object.keys(DETAIL_CHROME_ACTIONS) as DetailChromeActionId[]).forEach((id) => {
      if (!DETAIL_CHROME_ACTIONS[id].statuses.includes(status)) return;
      const reason = resolveDetailChromeUnsupportedReason(id, capabilities);
      if (!reason) return;
      byReason.set(reason, [...(byReason.get(reason) ?? []), id]);
    });
    return [...byReason.entries()].map(([reason, ids]) => ({ reason, ids }));
  }, [capabilities, status]);

  return (
    <Card elevation="none" className="shrink-0">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700">헤더·하단 컨트롤 바 구성</h3>
        {value && (
          <button
            type="button"
            onClick={reset}
            className="rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-600 hover:bg-gray-50"
          >
            기본값으로 초기화
          </button>
        )}
      </div>
      <p className="mb-3 text-xs text-gray-500">
        헤더·하단 컨트롤 바 버튼을 결재 진행 상태별로 구성합니다. (동작은 코드가 소유 —
        여기선 노출/배치/라벨만)
      </p>

      <p className="mb-3 text-xs text-gray-400">
        미리보기 상태:{' '}
        <span className="font-medium text-gray-600">{DETAIL_CHROME_STATUS_LABELS[status]}</span>{' '}
        (바에서 전환)
      </p>

      {/* 선택이 양식 미지원 버튼이면(저작 중 「위임 작성」을 끈 직후 등) 속성 대신 사유를 보여 준다 —
          바에서 사라진 버튼의 속성 패널만 남아 있으면 편집 결과가 어디에도 안 보인다. */}
      {selected && isUnsupportedByTemplate(selected, capabilities) ? (
        <div className="rounded-card border border-dashed border-gray-300 bg-gray-50 px-3 py-4 text-center text-xs text-gray-400">
          「{DETAIL_CHROME_ACTION_LABELS[selected]}」 —{' '}
          {resolveDetailChromeUnsupportedReason(selected, capabilities)}
        </div>
      ) : selected ? (
        <PropertyPanel
          actionId={selected}
          config={value}
          status={status}
          onPatch={(patch) => patchOverride(selected, patch)}
          onSetOverflow={(o) => setOverflow(selected, o)}
          onSetCondition={(field, val) => setCondition(selected, field, val)}
          onMove={(dir) => moveItem(selected, dir)}
          canMoveUp={moveInfo.canUp}
          canMoveDown={moveInfo.canDown}
          onRemove={
            DETAIL_CHROME_ACTIONS[selected].defaultHidden ? () => removeItem(selected) : undefined
          }
        />
      ) : (
        <div className="rounded-card border border-dashed border-gray-300 bg-gray-50 px-3 py-4 text-center text-xs text-gray-400">
          미리보기 바에서 버튼을 클릭해 편집하세요.
        </div>
      )}

      {/* 추가 가능한 버튼(opt-in) — 특정 보고서만 쓰는 선택 기능(예 현장 담당자 서명). 기본 미배치라
          클릭해 추가. 실제 동작 배선 전엔 미리보기 전용. */}
      {optInIds.length > 0 && (
        <div className="mt-3 rounded-card-sm border border-dashed border-emerald-300 bg-emerald-50/40 p-2.5">
          <p className="mb-1.5 text-[11px] font-medium text-emerald-600">
            추가 가능한 버튼 <span className="font-normal text-emerald-500/70">(선택 기능 · 미리보기 전용)</span>
          </p>
          <div className="flex flex-wrap gap-1.5">
            {optInIds.map((id) => (
              <button
                key={id}
                type="button"
                onClick={() => addItem(id)}
                className="rounded border border-dashed border-emerald-400 px-2 py-0.5 text-xs text-emerald-700 hover:bg-emerald-100"
              >
                + {DETAIL_CHROME_ACTION_LABELS[id]}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* 양식 미지원 버튼 — 배치·숨김 축이 아니라 **양식 능력** 축이라 선택 불가(비대화형).
          사유만 적고 사라지면 "왜 없지"에서 막히므로 출구(양식 설정의 「위임 작성」·양식 정책의
          「제출 후 흐름」)를 같이 적는다. 사유가 다른 버튼을 한 문장 아래 모으면 엉뚱한 출구를
          가리키게 되므로 **사유별로** 묶는다. */}
      {unsupportedGroups.length > 0 && (
        <Card padding="none" surface="soft" elevation="none" className="mt-3 p-2.5">
          <p className="mb-1 text-[11px] font-medium text-gray-400">이 양식에서 쓸 수 없는 버튼</p>
          {unsupportedGroups.map((group) => (
            <div key={group.reason} className="mb-1.5 last:mb-0">
              <div className="mb-1 flex flex-wrap gap-1.5">
                {group.ids.map((id) => (
                  <span
                    key={id}
                    className="rounded border border-dashed border-gray-300 px-2 py-0.5 text-xs text-gray-400"
                  >
                    {DETAIL_CHROME_ACTION_LABELS[id]}
                  </span>
                ))}
              </div>
              <p className="text-[10px] text-gray-400">{group.reason}</p>
            </div>
          ))}
        </Card>
      )}

      {/* 숨긴 버튼 — 이 상태에서 숨긴 optional(바에 안 뜸). 클릭해 선택 후 노출 토글로 재활성. */}
      {hiddenIds.length > 0 && (
        <Card padding="none" surface="soft" elevation="none" className="mt-3 p-2.5">
          <p className="mb-1.5 text-[11px] font-medium text-gray-400">이 상태에서 숨긴 버튼</p>
          <div className="flex flex-wrap gap-1.5">
            {hiddenIds.map((id) => (
              <button
                key={id}
                type="button"
                onClick={() => onSelect(id)}
                className={`rounded border border-dashed px-2 py-0.5 text-xs text-gray-400 line-through hover:border-gray-400 ${
                  selected === id ? 'border-amber-400 ring-1 ring-amber-300' : 'border-gray-300'
                }`}
              >
                {effectiveLabelOf(value, status, id)}
              </button>
            ))}
          </div>
        </Card>
      )}
    </Card>
  );
}

/** 선택된 단일 버튼의 상태별 속성 편집 패널. */
/** 오버플로우 정책 옵션 — 좁은 폭에서 이 버튼을 '⋯' 메뉴로 접을지. */
const OVERFLOW_OPTIONS: { value: DetailChromeOverflow; label: string }[] = [
  { value: 'auto', label: '자동(좁으면 메뉴)' },
  { value: 'always', label: '항상 메뉴' },
  { value: 'never', label: '항상 노출' },
];

function PropertyPanel({
  actionId,
  config,
  status,
  onPatch,
  onSetOverflow,
  onSetCondition,
  onMove,
  canMoveUp,
  canMoveDown,
  onRemove,
}: {
  actionId: DetailChromeActionId;
  config: DetailChromeConfig | undefined;
  status: DetailChromeStatus;
  onPatch: (patch: Partial<DetailChromeItemStatusOverride>) => void;
  onSetOverflow: (overflow: DetailChromeOverflow) => void;
  onSetCondition: (
    field: 'enableWhen' | 'visibleWhen' | 'disabledReason' | 'primaryWhen',
    val: string | undefined,
  ) => void;
  onMove: (dir: -1 | 1) => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
  /** opt-in(defaultHidden) 액션일 때만 — 제거(config 항목 삭제 → '추가 가능한 버튼'으로 복귀). */
  onRemove?: () => void;
}) {
  const meta = DETAIL_CHROME_ACTIONS[actionId];
  const item = config?.items.find((it) => it.actionId === actionId);
  const ov = item?.statusOverrides?.[status];
  const baseSlot = item?.slot ?? meta.defaultSlot;
  const baseOrder = item?.order ?? meta.defaultOrder;
  const hidden = ov?.hidden ?? false;
  // mandatory는 오버플로우 무관 항상 노출(never 강제), 그 외 base overflow(미지정=액션 기본 defaultOverflow ?? auto).
  const overflow: DetailChromeOverflow = meta.mandatory
    ? 'never'
    : (item?.overflow ?? meta.defaultOverflow ?? 'auto');

  return (
    <div className="rounded-card border border-brand-200 bg-brand-50/40 p-3">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-medium text-gray-700">
            {ov?.label ?? item?.label ?? DETAIL_CHROME_ACTION_LABELS[actionId]}
          </span>
          {meta.mandatory && (
            <span className="rounded bg-gray-200 px-1 text-[10px] font-medium text-gray-600">
              필수
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {/* 순서 이동(↑↓) — 같은 슬롯 내 인접 형제와 교환. */}
          <button
            type="button"
            onClick={() => onMove(-1)}
            disabled={!canMoveUp}
            title="위로(앞으로)"
            className="rounded border border-gray-300 bg-white px-1.5 py-0.5 text-xs text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-300"
          >
            ↑
          </button>
          <button
            type="button"
            onClick={() => onMove(1)}
            disabled={!canMoveDown}
            title="아래로(뒤로)"
            className="rounded border border-gray-300 bg-white px-1.5 py-0.5 text-xs text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-300"
          >
            ↓
          </button>
          {onRemove && (
            <button
              type="button"
              onClick={onRemove}
              title="이 버튼 제거(추가 가능한 버튼으로 복귀)"
              className="rounded border border-red-300 bg-white px-1.5 py-0.5 text-xs text-red-600 hover:bg-red-50"
            >
              제거
            </button>
          )}
          <span className="font-mono text-[11px] text-gray-400">{actionId}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {/* 노출(hidden) — mandatory는 숨김 불가(토글 비활성, 항상 노출). */}
        <label className="flex items-center gap-1.5 text-xs text-gray-600">
          <input
            type="checkbox"
            checked={meta.mandatory ? true : !hidden}
            disabled={meta.mandatory}
            onChange={(e) =>
              // 체크=노출(hidden 키 삭제), 해제=숨김(hidden:true).
              onPatch({ hidden: e.target.checked ? undefined : true })
            }
          />
          <span className="font-medium">노출</span>
          {meta.mandatory ? (
            <span className="text-[10px] text-gray-400">(숨김 불가)</span>
          ) : (
            ov?.hidden !== undefined && <OverrideBadge />
          )}
        </label>

        {/* 슬롯 */}
        <div className="flex items-center gap-1.5 text-xs text-gray-600">
          <span className="font-medium">배치 슬롯</span>
          <select
            value={ov?.slot ?? baseSlot}
            onChange={(e) => onPatch({ slot: e.target.value as DetailChromeSlot })}
            className="rounded border border-gray-300 px-1.5 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-brand-400"
          >
            {SLOT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {ov?.slot !== undefined && (
            <>
              <OverrideBadge />
              <ResetLink onClick={() => onPatch({ slot: undefined })} />
            </>
          )}
        </div>

        {/* 순서 */}
        <div className="flex items-center gap-1.5 text-xs text-gray-600">
          <span className="font-medium">순서</span>
          <input
            type="number"
            value={ov?.order ?? baseOrder}
            onChange={(e) =>
              onPatch({ order: e.target.value === '' ? undefined : Number(e.target.value) })
            }
            className="w-16 rounded border border-gray-300 px-1.5 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-brand-400"
          />
          {ov?.order !== undefined && (
            <>
              <OverrideBadge />
              <ResetLink onClick={() => onPatch({ order: undefined })} />
            </>
          )}
        </div>

        {/* 라벨 override */}
        <label className="flex items-center gap-1.5 text-xs text-gray-600">
          <span className="font-medium">라벨</span>
          <input
            type="text"
            value={ov?.label ?? ''}
            onChange={(e) => onPatch({ label: e.target.value || undefined })}
            placeholder={DETAIL_CHROME_ACTION_LABELS[actionId]}
            className="flex-1 rounded border border-gray-300 px-1.5 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-brand-400"
          />
          {ov?.label !== undefined && <OverrideBadge />}
        </label>

        {/* 오버플로우 정책(base, 상태 무관) — 좁은 폭(모바일/바형)에서 '⋯' 메뉴로 접을지. mandatory는 강제 노출. */}
        <div className="flex items-center gap-1.5 text-xs text-gray-600">
          <span className="font-medium">좁은 폭</span>
          <select
            value={overflow}
            disabled={meta.mandatory}
            onChange={(e) => onSetOverflow(e.target.value as DetailChromeOverflow)}
            className="rounded border border-gray-300 px-1.5 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-brand-400 disabled:bg-gray-100 disabled:text-gray-400"
          >
            {OVERFLOW_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {meta.mandatory && <span className="text-[10px] text-gray-400">(항상 노출)</span>}
        </div>
      </div>

      {/* 범용 게이팅 조건(base, 상태 무관) — 다른 요소의 완료/상태를 참조해 노출/활성 결정.
          예: 결재.활성 조건 = '현장 담당자 서명 완료'(contactSignatureComplete). 도메인 하드코딩 대신 조건 조립. */}
      <div className="mt-3 rounded-card-sm border border-gray-200 bg-white/60 p-2.5">
        <p className="mb-1.5 text-[11px] font-medium text-gray-500">
          게이팅 조건 <span className="font-normal text-gray-400">(다른 요소 상태 참조 — 범용)</span>
        </p>
        {/* 결재 드롭다운(submit/cancelSubmit)은 결재/결재 취소/결재 재전송으로 구성 — 조건은 주 액션 '결재'에
            적용. 취소·재전송은 코드가 진행 단계·권한으로 자동 활성/비활성(무결성 규칙이라 저작 대상 아님). */}
        {(actionId === 'submit' || actionId === 'cancelSubmit') && (
          <p className="mb-2 rounded bg-amber-50 px-2 py-1 text-[10px] text-amber-700">
            이 조건은 주 액션 <b>결재</b>에 적용됩니다. 결재 취소·재전송은 코드가 진행 단계·권한별로 자동
            활성/비활성합니다(무결성 규칙 — 저작 대상 아님).
          </p>
        )}
        {/* 코드 소유 기본 활성 조건(편집 불가) — 저작 '활성 조건'은 이 위에 AND로 추가. 재제출이 '활성 조건:
            없음'인데도 토글에 반응하는 이유(코드가 editedAfterRejection로 이미 게이팅). */}
        {CODE_ENABLE_CONDITIONS[actionId] && (
          <p className="mb-2 rounded bg-gray-100 px-2 py-1 text-[10px] text-gray-600">
            코드 기본 활성 조건: <b>{CODE_ENABLE_CONDITIONS[actionId]}</b> (편집 불가). 아래 활성 조건은 이
            위에 <b>AND</b>로 추가됩니다.
          </p>
        )}
        <div className="flex flex-col gap-2">
          <ConditionField
            label={CODE_ENABLE_CONDITIONS[actionId] ? '활성 조건(추가)' : '활성 조건'}
            value={item?.enableWhen}
            onChange={(v) => onSetCondition('enableWhen', v)}
            hint={
              CODE_ENABLE_CONDITIONS[actionId]
                ? '코드 기본 조건에 AND로 추가. 미지정=코드 조건만.'
                : '미충족 시 이 버튼(또는 결재 메뉴 항목) 비활성.'
            }
          />
          {item?.enableWhen && (
            <label className="flex items-center gap-1.5 text-xs text-gray-600">
              <span className="w-16 shrink-0 font-medium">비활성 사유</span>
              <input
                type="text"
                value={item?.disabledReason ?? ''}
                onChange={(e) => onSetCondition('disabledReason', e.target.value || undefined)}
                placeholder="예: 현장 담당자 서명 완료 후 결재 가능"
                className="flex-1 rounded border border-gray-300 px-1.5 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-brand-400"
              />
            </label>
          )}
          <ConditionField
            label="노출 조건"
            value={item?.visibleWhen}
            onChange={(v) => onSetCondition('visibleWhen', v)}
            hint="미충족 시 이 버튼 미표시."
          />
          <ConditionField
            label="강조 조건"
            value={item?.primaryWhen}
            onChange={(v) => onSetCondition('primaryWhen', v)}
            hint="충족 시 primary(강조), 미충족 시 secondary. 예: 결재는 결재 가능할 때만, 현장 담당자 서명은 서명 전(!contactSignatureComplete)까지."
          />
        </div>
      </div>
    </div>
  );
}
